import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import Modal from '../components/Modal';

const SettingsDashboard = () => {
    const { authFetch, hasPermission, user, depos, setDepos } = useContext(AppContext);
    const { t } = useTranslation();

    const [settings, setSettings] = useState({ default_tva: '' });
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [matrix, setMatrix] = useState({});
    const [loading, setLoading] = useState(true);

    // Branch Management State
    const [isBranchModalOpen, setIsBranchModalOpen] = useState(false);
    const [newBranch, setNewBranch] = useState({ name: '', location: '' });

    const fetchDepos = () => {
        authFetch('/api/depos')
            .then(r => r.json())
            .then(d => setDepos(Array.isArray(d) ? d : []));
    };

    const handleAddBranch = async (e) => {
        e.preventDefault();
        try {
            const res = await authFetch('/api/depos', { method: 'POST', body: JSON.stringify(newBranch) });
            if (res.ok) {
                setNewBranch({ name: '', location: '' });
                fetchDepos();
            } else {
                alert(t('Failed to add branch'));
            }
        } catch (err) {
            alert(t('Error adding branch'));
        }
    };

    const handleDeleteBranch = async (id) => {
        if (!window.confirm(t('Delete this branch?'))) return;
        try {
            const res = await authFetch(`/api/depos/${id}`, { method: 'DELETE' });
            if (!res.ok) {
                const data = await res.json();
                alert(t(data.message || 'Error deleting branch'));
                return;
            }
            fetchDepos();
        } catch (err) {
            alert(t('Error deleting branch'));
        }
    };

    const isSuperAdmin = user?.role === 'Admin' || user?.role_name === 'Super Admin';

    useEffect(() => {
        if (!isSuperAdmin && !hasPermission('view_settings')) return;

        const fetchData = async () => {
            try {
                const [settingsRes, rolesRes] = await Promise.all([
                    authFetch('/api/settings'),
                    authFetch('/api/roles?all=1')
                ]);

                if (settingsRes.ok) {
                    setSettings(await settingsRes.json());
                }

                if (rolesRes.ok) {
                    const data = await rolesRes.json();
                    if (data && data.roles) {
                        setRoles(data.roles);
                        setPermissions(data.permissions);
                        setMatrix(data.matrix || {});
                    }
                }
            } catch (err) {
                console.error('Failed to load settings:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [authFetch, hasPermission, isSuperAdmin]);

    const handleSettingChange = (key, value) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    const saveSettings = async () => {
        try {
            const res = await authFetch('/api/settings', {
                method: 'PUT',
                body: JSON.stringify(settings)
            });
            if (res.ok) {
                alert(t('Settings saved successfully'));
            } else {
                alert(t('Failed to save settings'));
            }
        } catch (err) {
            console.error(err);
        }
    };

    const togglePermission = async (roleId, permId, currentVal) => {
        const isAssigned = !currentVal;

        // Optimistic UI update
        setMatrix(prev => ({
            ...prev,
            [roleId]: {
                ...(prev[roleId] || {}),
                [permId]: isAssigned
            }
        }));

        try {
            const res = await authFetch('/api/roles', {
                method: 'POST',
                body: JSON.stringify({ role_id: roleId, permission_id: permId, is_assigned: isAssigned })
            });

            if (!res.ok) {
                throw new Error('Failed to update DB');
            }
        } catch (err) {
            console.error(err);
            alert(t('Error updating permission'));
            // Revert on error
            setMatrix(prev => ({
                ...prev,
                [roleId]: {
                    ...(prev[roleId] || {}),
                    [permId]: currentVal
                }
            }));
        }
    };

    if (!isSuperAdmin && !hasPermission('view_settings')) {
        return <div className="p-8 text-center text-red-500 font-bold">{t('Access Denied')}</div>;
    }

    if (loading) return <div>{t('Loading...')}</div>;

    // Group permissions by module
    const groupedPermissions = permissions.reduce((acc, perm) => {
        if (!acc[perm.module]) acc[perm.module] = [];
        acc[perm.module].push(perm);
        return acc;
    }, {});

    return (
        <div className="container mx-auto p-4 space-y-8">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-black text-gray-800">{t('System & Security Configurations')}</h2>
            </div>

            {/* Global Settings Block */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Company & Receipt Branding Settings */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col justify-between">
                    <div>
                        <h3 className="text-xl font-bold text-gray-800 border-b pb-3 mb-4 flex items-center gap-2">
                            <span>🧾</span> {t('Company Branding & Receipt Details')}
                        </h3>
                        <p className="text-xs text-gray-500 mb-4">{t('Configure company details, logo, and legal headers printed on receipts and invoices.')}</p>

                        <div className="space-y-4">
                            {/* Logo Upload & Preview */}
                            <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">{t('Company Logo')}</label>
                                <div className="flex items-center gap-4">
                                    {settings.company_logo ? (
                                        <div className="relative group w-20 h-20 bg-white border rounded-lg p-1 flex items-center justify-center shadow-sm">
                                            <img src={settings.company_logo} alt="Company Logo" className="max-h-full max-w-full object-contain" />
                                            <button
                                                type="button"
                                                onClick={() => handleSettingChange('company_logo', '')}
                                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs font-bold flex items-center justify-center shadow"
                                                title="Remove Logo"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="w-20 h-20 bg-gray-200 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400 text-xs">
                                            <span>📷</span>
                                            <span>{t('No Logo')}</span>
                                        </div>
                                    )}

                                    <div className="flex-1 space-y-2">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                    const reader = new FileReader();
                                                    reader.onloadend = () => handleSettingChange('company_logo', reader.result);
                                                    reader.readAsDataURL(file);
                                                }
                                            }}
                                            className="block w-full text-xs text-gray-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                                        />
                                        <input
                                            type="text"
                                            placeholder="Or enter Image URL..."
                                            className="w-full text-xs border border-gray-300 rounded p-1.5 focus:ring-indigo-500 focus:border-indigo-500"
                                            value={settings.company_logo || ''}
                                            onChange={(e) => handleSettingChange('company_logo', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">{t('Company Name')}:</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. SUGIMCO"
                                        className="w-full border-gray-300 rounded-md text-sm p-2 shadow-sm focus:ring-blue-500"
                                        value={settings.company_name || ''}
                                        onChange={(e) => handleSettingChange('company_name', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">{t('Tagline / Subtitle')}:</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. SUPER MARCHE Yazbek"
                                        className="w-full border-gray-300 rounded-md text-sm p-2 shadow-sm focus:ring-blue-500"
                                        value={settings.company_subtitle || ''}
                                        onChange={(e) => handleSettingChange('company_subtitle', e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">{t('NIF (Tax ID)')}:</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. M 243623 A 001"
                                        className="w-full border-gray-300 rounded-md text-sm p-2 shadow-sm focus:ring-blue-500"
                                        value={settings.company_nif || ''}
                                        onChange={(e) => handleSettingChange('company_nif', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">{t('RC (Trade Register)')}:</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. 2003 B 54"
                                        className="w-full border-gray-300 rounded-md text-sm p-2 shadow-sm focus:ring-blue-500"
                                        value={settings.company_rc || ''}
                                        onChange={(e) => handleSettingChange('company_rc', e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">{t('Primary Phone')}:</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. 21812160"
                                        className="w-full border-gray-300 rounded-md text-sm p-2 shadow-sm focus:ring-blue-500"
                                        value={settings.company_phone || ''}
                                        onChange={(e) => handleSettingChange('company_phone', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">{t('Secondary Phone')}:</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. +23670500060"
                                        className="w-full border-gray-300 rounded-md text-sm p-2 shadow-sm focus:ring-blue-500"
                                        value={settings.company_phone2 || ''}
                                        onChange={(e) => handleSettingChange('company_phone2', e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">{t('Address / City')}:</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. BANGUI (RCA)"
                                        className="w-full border-gray-300 rounded-md text-sm p-2 shadow-sm focus:ring-blue-500"
                                        value={settings.company_address || ''}
                                        onChange={(e) => handleSettingChange('company_address', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">{t('Email Address')}:</label>
                                    <input
                                        type="email"
                                        placeholder="e.g. supermarcheYazbek@yahoo.com"
                                        className="w-full border-gray-300 rounded-md text-sm p-2 shadow-sm focus:ring-blue-500"
                                        value={settings.company_email || ''}
                                        onChange={(e) => handleSettingChange('company_email', e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Thermal Receipt Paper Width */}
                            <div className="border border-indigo-100 bg-indigo-50/50 rounded-lg p-3 mt-3">
                                <div className="flex justify-between items-center mb-1">
                                    <label className="block text-xs font-bold text-gray-800 uppercase tracking-wider">{t('Receipt Paper Width (mm)')}</label>
                                    <span className="text-xs font-bold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded">
                                        {settings.receipt_width || '65'} mm
                                    </span>
                                </div>
                                <p className="text-[11px] text-gray-500 mb-2">
                                    {t('Adjust the printable width for thermal receipt printers. Use 64-66mm if prices, total, or TVA get cut off on the right.')}
                                </p>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <input
                                        type="number"
                                        min="48"
                                        max="80"
                                        step="1"
                                        placeholder="65"
                                        className="w-20 border-gray-300 rounded-md text-sm p-1.5 shadow-sm focus:ring-blue-500 font-bold text-center"
                                        value={settings.receipt_width || '65'}
                                        onChange={(e) => handleSettingChange('receipt_width', e.target.value)}
                                    />
                                    <div className="flex gap-1 flex-wrap">
                                        {['58', '64', '65', '68', '72'].map((w) => (
                                            <button
                                                key={w}
                                                type="button"
                                                onClick={() => handleSettingChange('receipt_width', w)}
                                                className={`px-2 py-1 text-xs rounded font-bold border transition ${(settings.receipt_width || '65') === w
                                                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                                                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {w}mm {w === '65' ? `(${t('Recommended')})` : ''}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Global Variables & Branches */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col justify-between">
                    <div>
                        <h3 className="text-xl font-bold text-gray-800 border-b pb-3 mb-4 flex items-center gap-2">
                            <span>⚙️</span> {t('Global System Variables')}
                        </h3>

                        <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-4">
                                <label className="font-semibold text-gray-700 w-36 text-xs">{t('Default TVA (%)')}:</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className="form-control flex-1 focus:ring-blue-500 focus:border-blue-500 rounded-md shadow-sm border p-2 text-sm"
                                    value={settings.default_tva || ''}
                                    onChange={(e) => handleSettingChange('default_tva', e.target.value)}
                                />
                            </div>
                            <div className="flex items-center gap-4">
                                <label className="font-semibold text-gray-700 w-36 text-xs">{t('POS Warehouse')}:</label>
                                <select
                                    className="form-control flex-1 focus:ring-blue-500 focus:border-blue-500 rounded-md shadow-sm border p-2 text-sm"
                                    value={settings.pos_depo_id || ''}
                                    onChange={(e) => handleSettingChange('pos_depo_id', e.target.value)}
                                >
                                    <option value="">{t('Default User Warehouse')}</option>
                                    {depos && depos.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="mt-8 border-t pt-6">
                            <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                                <span className="text-xl">🏢</span> {t('System Branches')}
                            </h4>
                            <p className="text-sm text-gray-500 mb-4">{t('Manage physical warehouse locations across the entire system.')}</p>
                            <button
                                onClick={() => setIsBranchModalOpen(true)}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-lg font-bold shadow-md transition flex items-center gap-2"
                            >
                                <span>⚙️</span> {t('Manage Branches')}
                            </button>
                        </div>
                    </div>

                    <div className="mt-8 border-t pt-4">
                        <button
                            className="bg-indigo-600 hover:bg-indigo-700 text-white font-black py-3 px-8 rounded-lg transition-colors w-full shadow-lg shadow-indigo-600/20 text-sm"
                            onClick={saveSettings}
                        >
                            💾 {t('Save All Settings')}
                        </button>
                    </div>
                </div>
            </div>

            {/* RBAC Matrix */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 overflow-x-auto">
                <h3 className="text-xl font-bold text-gray-800 border-b pb-3 mb-4">{t('Role-Based Access Matrix')}</h3>

                <div className="w-full overflow-x-auto max-w-full">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="p-3 border font-bold text-indigo-900 w-1/4">{t('Modules & Permissions')}</th>
                                {roles.map(r => (
                                    <th key={r.id} className="p-3 border font-bold text-center text-sm break-words" style={{ minWidth: '100px' }}>
                                        {r.name}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {Object.keys(groupedPermissions).map(moduleName => (
                                <React.Fragment key={moduleName}>
                                    <tr className="bg-indigo-50">
                                        <td colSpan={roles.length + 1} className="p-2 border font-bold text-indigo-800">
                                            {moduleName}
                                        </td>
                                    </tr>
                                    {groupedPermissions[moduleName].map(perm => (
                                        <tr key={perm.id} className="hover:bg-gray-50">
                                            <td className="p-3 border pl-6">
                                                <div className="font-semibold text-gray-700">{perm.name}</div>
                                                <div className="text-xs text-gray-500">{perm.description}</div>
                                            </td>
                                            {roles.map(role => {
                                                const isChecked = !!(matrix[role.id] && matrix[role.id][perm.id]);
                                                return (
                                                    <td key={role.id} className="p-3 border text-center align-middle">
                                                        <label className="relative inline-flex items-center cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                className="sr-only peer"
                                                                checked={isChecked}
                                                                onChange={() => togglePermission(role.id, perm.id, isChecked)}
                                                            />
                                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                                        </label>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── BRANCH MANAGEMENT MODAL ─────────────────────── */}
            <Modal isOpen={isBranchModalOpen} onClose={() => setIsBranchModalOpen(false)} title={t('Manage Branches')}>
                <div className="space-y-6">
                    {/* Create New Branch */}
                    <form onSubmit={handleAddBranch} className="bg-[#f8fafc] border border-gray-200 rounded-xl p-5 shadow-sm">
                        <h4 className="font-bold text-gray-800 flex items-center gap-2 mb-4">
                            <span className="text-lg">✨</span> {t('Create New Branch')}
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t('Branch Name')} <span className="text-red-500">*</span></label>
                                <input required className="w-full border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="e.g. Main Depot" value={newBranch.name} onChange={e => setNewBranch({ ...newBranch, name: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t('Location')}</label>
                                <input className="w-full border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="e.g. Downtown" value={newBranch.location} onChange={e => setNewBranch({ ...newBranch, location: e.target.value })} />
                            </div>
                        </div>
                        <button type="submit" className="mt-4 w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 rounded-lg font-bold shadow-md shadow-indigo-600/20 transition-all flex justify-center items-center gap-2">
                            <span>➕</span> {t('Add Branch')}
                        </button>
                    </form>

                    {/* Active Branches List */}
                    <div>
                        <h4 className="font-bold text-gray-800 flex items-center gap-2 mb-3">
                            <span className="text-lg">🏢</span> {t('Active Branches')} <span className="bg-indigo-100 text-indigo-800 text-xs py-0.5 px-2 rounded-full ml-1">{depos?.length || 0}</span>
                        </h4>
                        <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-2 custom-scrollbar">
                            {depos && depos.map((d, index) => {
                                const isPrimary = Number(settings.pos_depo_id || 1) === Number(d.id);
                                return (
                                    <div key={d.id} className={`group flex items-center justify-between bg-white border ${isPrimary ? 'border-green-300 shadow-sm' : 'border-gray-100 hover:border-indigo-200'} rounded-xl p-4 transition-all shadow-sm hover:shadow-md`}>
                                        <div className="flex flex-col">
                                            <div className="flex items-center gap-2">
                                                <span className={`font-black ${isPrimary ? 'text-green-800' : 'text-gray-800'}`}>{d.name}</span>
                                                {isPrimary && <span className="text-[10px] bg-green-100 text-green-700 font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">{t('Primary')}</span>}
                                            </div>
                                            {d.location && <span className="text-xs font-medium text-gray-500 mt-0.5">📍 {d.location}</span>}
                                        </div>

                                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            {!isPrimary && (
                                                <button
                                                    onClick={async () => {
                                                        const updatedSettings = { ...settings, pos_depo_id: d.id };
                                                        setSettings(updatedSettings);
                                                        try {
                                                            const res = await authFetch('/api/settings', { method: 'PUT', body: JSON.stringify(updatedSettings) });
                                                            if (res.ok) alert(t('Primary branch updated!'));
                                                        } catch (err) { }
                                                    }}
                                                    className="bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white px-2 py-1 rounded text-xs font-bold transition-colors"
                                                    title={t('Set as Primary')}
                                                >
                                                    ⭐ {t('Set Primary')}
                                                </button>
                                            )}
                                            {d.id !== 1 ? (
                                                <button
                                                    onClick={() => handleDeleteBranch(d.id)}
                                                    className="bg-red-50 text-red-600 hover:bg-red-600 hover:text-white w-8 h-8 rounded-full flex items-center justify-center transition-all"
                                                    title={t('Delete Branch')}
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                </button>
                                            ) : (
                                                <span className="text-[10px] text-gray-400 font-bold uppercase mx-2" title="System default branch cannot be deleted">🔒 System</span>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                            {depos?.length === 0 && (
                                <div className="text-center py-8 text-gray-400 bg-gray-50 border border-dashed border-gray-200 rounded-xl">
                                    <p className="text-sm font-medium">{t('No branches found.')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>

        </div>
    );
};

export default SettingsDashboard;
